import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import styled from 'styled-components'
import {
  LiquidityInputItem,
  Page,
  PageContent,
  PageHeader,
  SettingsButton,
  Plus,
  Receive,
  DisabledMessage,
} from '../../common'
import { PoolNavigation } from '../pool/PoolNavigation'
import { BackNavigation } from './BackNavigation'
import { LiquidityApprovalButton } from '../../common/approval/LiquidityApprovalButton'
import { Currency, CurrencyValue } from '../../model'
import { RemoveLiquidityButton } from './RemoveLiquidityButton'
import { LiquidityState, useRemoveLiquidity } from './useRemoveLiquidity'
import { useBalance, useEthers } from '../../hooks'
import { ChainId, Colors, DISABLE_WITHDRAW } from '../../constants'
import { logAmplitudeEvent } from '../../utils'

export function RemoveLiquidity({
  match: {
    params: { pairAddress },
  },
}: RouteComponentProps<{ pairAddress: string }>) {
  const {
    liquidityValue,
    liquidityInputValue,
    liquidityBalance,
    pair,
    firstCurrencyValue,
    firstUsdValue,
    firstUsdValue18,
    secondCurrencyValue,
    secondUsdValue,
    secondUsdValue18,
    state,
    showOutput,
    currencies,
    setLiquidityInputValue,
    setLiquidityInputMax,
  } = useRemoveLiquidity({ pairAddress })

  function onPairChange(currency: Currency, other: Currency) {
    setLiquidityInputValue('')
    currencies.setCurrencies(currency, other)
    logAmplitudeEvent('Withdraw: Change Pair', {
      pair: `${currency.symbol}-${other.symbol}`,
    })
  }

  function printCurrencyValue(currencyValue: CurrencyValue) {
    return showOutput && !currencyValue.value.isZero() ? currencyValue.format() : '-'
  }

  const currencyBalance = useBalance(currencies.first)
  const otherCurrencyBalance = useBalance(currencies.second)

  const { chainId = ChainId.Mainnet } = useEthers()
  const disabled = pair && DISABLE_WITHDRAW.includes(pair.address.toLowerCase())
  if (chainId === ChainId.Mainnet && disabled) {
    return (
      <Page>
        <PageContent>
          <PageHeader label="Withdraw" currency={currencies.first} other={currencies.second} onChange={onPairChange} />
          <PoolNavigation />
          <BackNavigationStyled />
          <DisabledMessage>
            <p>Withdraw on this pair is currently disabled.</p>
            <p>Please check back later or visit our Discord to learn more.</p>
          </DisabledMessage>
        </PageContent>
      </Page>
    )
  }

  return (
    <Page>
      <PageContent>
        <PageHeader label="Withdraw" currency={currencies.first} other={currencies.second} onChange={onPairChange} />
        <PoolNavigation />
        <BackNavigationStyled />
        <LiquidityInputItemStyled
          pair={pair}
          label="You provide"
          value={liquidityInputValue}
          liquidityBalance={liquidityBalance}
          onValueChange={(value) => {
            setLiquidityInputValue(value)
            logAmplitudeEvent('Withdraw: Input LP', {
              pair: `${currencies.first.symbol}-${currencies.second.symbol}`,
              value,
            })
          }}
          onMax={setLiquidityInputMax}
        />
        <Hr />
        <ReceiveStyled
          pair={pair}
          symbol={currencies.first.symbol}
          rate={firstUsdValue || '-'}
          value={printCurrencyValue(firstCurrencyValue)}
          balance={currencyBalance && showOutput ? currencyBalance.formatWithSymbol() : '-'}
        />
        <PlusContainer>
          <PlusStyled />
        </PlusContainer>
        <Receive
          pair={pair}
          symbol={currencies.second.symbol}
          rate={secondUsdValue || '-'}
          value={printCurrencyValue(secondCurrencyValue)}
          balance={otherCurrencyBalance && showOutput ? otherCurrencyBalance.formatWithSymbol() : '-'}
        />
        {pair && state === LiquidityState.NEED_APPROVAL && <LiquidityApprovalButton pair={pair} />}
        <RemoveLiquidityButton
          state={state}
          onSubmit={() => setLiquidityInputValue('')}
          firstValue={firstCurrencyValue}
          secondValue={secondCurrencyValue}
          firstUsdValue18={firstUsdValue18}
          secondUsdValue18={secondUsdValue18}
          liquidity={liquidityValue}
          pair={pair}
        />
        <SettingsButton />
      </PageContent>
    </Page>
  )
}

const BackNavigationStyled = styled(BackNavigation)`
  margin: 15px 0 15px 0;
`

const LiquidityInputItemStyled = styled(LiquidityInputItem)`
  margin-top: 20px;
`

const Hr = styled.hr`
  margin: 15px 20px;
  padding: 0;
  display: block;
  height: 1px;
  background-color: ${Colors.LightestGray};
  border: 0;
`

const ReceiveStyled = styled(Receive)`
  margin-top: 15px;
`

const PlusContainer = styled.div`
  text-align: center;
`

const PlusStyled = styled(Plus)`
  width: 10px;
`
